// TAT Setup — platform-level static reference data only.
// Merchant-specific data (couriers, STs, TAT values, standard TAT) comes from APIs.

export const STAGES = [
  { id: 0, short: 'Pricing Submitted', label: 'Pricing Submitted', time: '<1 min', explainer: 'Pricing commit complete — TAT module opens carrying merchant context forward from the Pricing Setup.' },
  { id: 1, short: 'Profile', label: 'Operating Profile', time: '1 min', explainer: 'Pick the essentials — freight direction and mode × size. The service types in scope resolve automatically.' },
  { id: 2, short: 'Upload TATs', label: 'Upload TATs', time: '3 min', explainer: 'Download the Excel template for each service type, fill in Min and Max TAT for all 5 zones, then upload. Supports both Blended and Courier-wise modes.' },
  { id: 3, short: 'Review', label: 'Review & Validate', time: '1 min', explainer: 'Block submit on validation failures (empty / negative / Min > Max). Surface deviation metrics vs standard.' },
  { id: 4, short: 'Submit & Upload', label: 'Submit & Upload', time: '2 min', explainer: 'Two-step publish: generate-and-download the per-account-code zip locally, then push each CSV to the upload API.' },
  { id: 5, short: 'Done', label: 'Confirmation', time: '<1 min', explainer: 'TAT files published. Confirmation card with reset to restart for the next merchant.' },
];

// ── Operating Profile ─────────────────────────────────────────────────────────

export const TAT_FREIGHT_DIRECTIONS = [
  { key: 'forward', label: 'Forward' },
  { key: 'reverse_noqc', label: 'Reverse · No QC' },
  { key: 'reverse_qc', label: 'Reverse · With QC' },
];

export const TAT_MODE_ROWS = [
  { k: 'surface', lbl: 'Surface' },
  { k: 'air', lbl: 'Air' },
  { k: 'sdd', lbl: 'SDD' },
  { k: 'sc_ndd', lbl: 'Same-City NDD' },
  { k: 'intercity_ndd', lbl: 'Inter-City NDD' },
  { k: 'h2to4', lbl: '2–4 hour delivery' },
  { k: 'sub2h', lbl: 'Sub-2 hour' },
  { k: 'hv', lbl: 'High Value' },
];

const MODE_ST_MAP = {
  surface: ['FWD_SURFACE_REG', 'FWD_SURFACE_LARGE'],
  air: ['FWD_AIR_REG', 'FWD_AIR_LARGE'],
  sdd: ['SDD_FWD_REG', 'SDD_FWD_LARGE'],
  sc_ndd: ['SC_NDD_FWD_REG', 'SC_NDD_FWD_LARGE'],
  intercity_ndd: ['INTERCITY_NDD_REG', 'INTERCITY_NDD_LARGE'],
  h2to4: ['H2TO4', 'H2TO4'],
  sub2h: ['SUB2H', 'SUB2H'],
  hv: ['HV_FWD_REG', 'HV_FWD_LARGE'],
};

export function derivedStsForMode(mode, reg, large) {
  const pair = MODE_ST_MAP[mode] || [];
  const arr = [];
  if (reg && pair[0]) arr.push(pair[0]);
  if (large && pair[1] && !arr.includes(pair[1])) arr.push(pair[1]);
  return arr;
}

export function resolveStsFromProfile(profile) {
  const out = [];
  const modes = profile.modes;
  const push = (code, derivation) => {
    if (!out.find((o) => o.code === code)) out.push({ code, derivation });
  };
  if (profile.directions.forward) {
    if (modes.surface.reg) push('FWD_SURFACE_REG', 'Forward + Surface + Regular');
    if (modes.surface.large) push('FWD_SURFACE_LARGE', 'Forward + Surface + Large');
    if (modes.air.reg) push('FWD_AIR_REG', 'Forward + Air + Regular');
    if (modes.air.large) push('FWD_AIR_LARGE', 'Forward + Air + Large');
    if (modes.sdd.reg) push('SDD_FWD_REG', 'Forward + SDD + Regular');
    if (modes.sdd.large) push('SDD_FWD_LARGE', 'Forward + SDD + Large');
    if (modes.sc_ndd.reg) push('SC_NDD_FWD_REG', 'Forward + Same-City NDD + Regular');
    if (modes.sc_ndd.large) push('SC_NDD_FWD_LARGE', 'Forward + Same-City NDD + Large');
    if (modes.intercity_ndd.reg) push('INTERCITY_NDD_REG', 'Forward + Inter-City NDD + Regular');
    if (modes.intercity_ndd.large) push('INTERCITY_NDD_LARGE', 'Forward + Inter-City NDD + Large');
    if (modes.h2to4.reg || modes.h2to4.large) push('H2TO4', 'Forward + 2–4h Express');
    if (modes.sub2h.reg || modes.sub2h.large) push('SUB2H', 'Forward + Sub-2h Express');
    if (modes.hv.reg) push('HV_FWD_REG', 'High-Value Forward + Regular');
    if (modes.hv.large) push('HV_FWD_LARGE', 'High-Value Forward + Large');
  }
  if (profile.directions.reverse_noqc) {
    if (modes.surface.reg || modes.surface.large || modes.air.reg) push('REV_REG_NOQC', 'Reverse + Regular + No QC');
    if (modes.surface.large) push('REV_LARGE_NOQC', 'Reverse + Large + No QC');
    if (modes.hv.reg) push('HV_REVERSE_REG', 'High-Value Reverse + Regular');
    if (modes.hv.large) push('HV_REVERSE_LARGE', 'High-Value Reverse + Large');
  }
  if (profile.directions.reverse_qc) {
    if (modes.surface.reg || modes.surface.large || modes.air.reg) push('REV_REG_QC', 'Reverse + Regular + With QC');
    if (modes.surface.large) push('REV_LARGE_QC', 'Reverse + Large + With QC');
  }
  return out;
}

// ── Platform reference data ───────────────────────────────────────────────────

// Derives a display group label from a serviceabilityGroups API group object.
// Replaces the hardcoded TAXONOMY[].group field.
export function groupLabelFromApiGroup(g) {
  if (!g) return 'Other';
  switch (g.category) {
    case 'HIGH_VALUE': return g.direction === 'RVP' ? 'HV Reverse' : 'HV Forward';
    case 'REVERSE':    return g.weightBand === 'LARGE' ? 'Reverse Large' : 'Reverse';
    case 'HYPERLOCAL': return 'Hyperlocal';
    case 'SDD_NDD':    return g.weightBand === 'LARGE' ? 'Forward Large' : 'Forward';
    default:           return g.weightBand === 'LARGE' ? 'Forward Large' : 'Forward'; // FORWARD
  }
}

// service_type_master spells some codes differently from the wizard's ids
// (SC-NDD_FWD_REG vs SC_NDD_FWD_REG, Intercity_NDD_REG vs INTERCITY_NDD_REG),
// so every group lookup normalises rather than comparing raw strings — an exact
// match returned nothing for the NDD service types.
export function normalizeStCode(code) {
  return String(code || '').toUpperCase().replace(/-/g, '_');
}

// The serviceabilityGroups entry for an ST code, matched on the normalised code.
export function findStGroup(groups, stCode) {
  const want = normalizeStCode(stCode);
  return (groups || []).find((g) => normalizeStCode(g.code) === want) || null;
}

// Look up display name for an ST code from the serviceabilityGroups array.
// Falls back to the code itself when the API hasn't loaded yet.
export function stNameFromGroups(code, groups) {
  return findStGroup(groups, code)?.name || code;
}

// Keep only the account codes the merchant has pricing for. pricedKeys is the
// lowercased set of cplist values from merchant.sla (tatWizard.pricedKeys).
//
// cplist holds the courier's prozo_id (MerchantServiceImpl#saveRateCard writes the
// resolved prozo_id, falling back to the account code label only when it cannot be
// resolved), so prozoId is the key that actually matches — `DELHIVERY_PRZ_7` is
// neither the account code (`Delhivery SURFACE`) nor the parent (`Delhivery`).
// Matching on cpAccountCode/parent alone dropped every courier and left the
// courier-wise TAT editor showing "No couriers mapped for this service type".
// Empty pricedKeys = pricing not loaded → return the codes unchanged (fallback).
export function filterPricedCodes(linkedCodes, pricedKeys) {
  const set = pricedKeys instanceof Set ? pricedKeys : new Set(pricedKeys || []);
  if (!set.size) return linkedCodes || [];
  // Guard the empty key: a linkedCode missing prozoId/parent must not be matched
  // by a stray '' in pricedKeys.
  const has = (v) => !!v && set.has(String(v).toLowerCase());
  return (linkedCodes || []).filter(
    (lc) => has(lc.prozoId) || has(lc.cpAccountCode) || has(lc.parent),
  );
}

export const ZONES = [
  { id: 1, name: 'Zone 1 · Local', desc: 'Within city / Local pincodes' },
  { id: 2, name: 'Zone 2 · Regional', desc: 'Within state / Regional' },
  { id: 3, name: 'Zone 3 · Metro', desc: 'Metro to Metro pairs' },
  { id: 4, name: 'Zone 4 · ROI', desc: 'Rest of India' },
  { id: 5, name: 'Zone 5 · Special', desc: 'Special zones · NE · J&K · Islands' },
];

// TAXONOMY removed — service type names and groups come from
// GET /api/order/serviceability/intel/setup/groups via useGetServiceabilityGroupsQuery().
// Use stNameFromGroups(code, groups) and groupLabelFromApiGroup(apiGroup) instead.

// Upload error catalogue — static platform reference, not merchant-specific.
export const ERROR_MODES = [
  { code: 422, mode: 'cp_not_enabled', label: 'Courier not enabled', resolution: 'Trigger CP enablement workflow first.' },
  { code: 422, mode: 'sla_cap', label: 'Exceeds SLA cap', resolution: 'Reduce Max TAT or raise SLA-exception ticket.' },
  { code: 422, mode: 'qc_profile_missing', label: 'QC profile missing', resolution: 'Configure QC profile on CP master.' },
  { code: 422, mode: 'min_gt_max', label: 'Min greater than Max', resolution: 'Edit in UI (also blocked client-side).' },
  { code: 422, mode: 'zone_mapping_missing', label: 'Zone mapping missing', resolution: 'Push zone mapping for CP, then retry.' },
  { code: 422, mode: 'cp_inactive', label: 'CP marked inactive', resolution: 'Re-activate CP at master level.' },
  { code: 422, mode: 'unknown_courier_code', label: 'Unknown courier code', resolution: 'Verify account code regex; create at CP master if new.' },
  { code: 422, mode: 'csv_schema_mismatch', label: 'CSV schema mismatch', resolution: 'Header/row count broken — regenerate from UI.' },
  { code: 422, mode: 'merchant_not_active', label: 'Merchant not active', resolution: 'Approve merchant onboarding first.' },
  { code: 422, mode: 'tat_negative', label: 'Negative TAT value', resolution: 'Edit in UI (also blocked client-side).' },
  { code: 413, mode: 'payload_too_large', label: 'Payload too large', resolution: 'Reduce file size or split rows.' },
  { code: 401, mode: 'auth_failed', label: 'Auth token failed', resolution: 'Re-authenticate the session and retry.' },
  { code: 429, mode: 'rate_limited', label: 'Rate-limited', resolution: 'Back off and retry.' },
  { code: 500, mode: 'server_error', label: 'Server error', resolution: 'Transient — retry. If persists, raise with platform team.' },
  { code: 504, mode: 'network_timeout', label: 'Network timeout', resolution: 'Retry — if repeated, check network or contact platform team.' },
];

// ── Pure utility helpers ──────────────────────────────────────────────────────

export const cellKey = (st, parent, z, bound) => `${st}__${parent}__z${z}_${bound}`;
export const blendedKey = (st, z, bound) => `__BLEND__${st}__z${z}_${bound}`;

export function devClass(std, val) {
  if (std == null || val == null || val === '' || Number.isNaN(Number(val))) return '';
  if (Number(val) > std) return 'higher';
  if (Number(val) < std) return 'lower';
  return '';
}

// Deviation summary vs industry standard TAT (API 5 format: { ST: { "1":[min,max], ... } }).
// Iterates over actual tat/tatBlended keys — no hardcoded courier list.
export function computeDeviations(tat, tatBlended, modeByST, activeSts, standardTat, groups) {
  const detail = [];
  let total = 0; let higher = 0; let lower = 0; let zeros = 0; let empty = 0;
  (activeSts || []).forEach((st) => {
    const std = standardTat?.[st] || null;
    const mode = modeByST?.[st] || 'courier_wise';
    const stName = stNameFromGroups(st, groups);
    const checkCell = (rowLabel, row) => {
      for (let z = 1; z <= 5; z += 1) {
        ['min', 'max'].forEach((b) => {
          const v = row[`z${z}_${b}`];
          const stdVal = std?.[String(z)]?.[b === 'min' ? 0 : 1] ?? null;
          total += 1;
          if (v === null || v === undefined || v === '') { empty += 1; }
          else if (Number(v) === 0) { zeros += 1; }
          else if (stdVal != null && Number(v) > stdVal) {
            higher += 1;
            detail.push({ service_type: st, st_name: stName, zone: z, bound: b, entered: v, standard: stdVal, delta: v - stdVal, direction: 'higher', row_label: rowLabel });
          } else if (stdVal != null && Number(v) < stdVal) {
            lower += 1;
            detail.push({ service_type: st, st_name: stName, zone: z, bound: b, entered: v, standard: stdVal, delta: v - stdVal, direction: 'lower', row_label: rowLabel });
          }
        });
      }
    };
    if (mode === 'blended') {
      checkCell('Blended', tatBlended[st] || {});
    } else {
      Object.keys(tat[st] || {}).forEach((parent) => checkCell(parent, tat[st][parent] || {}));
    }
  });
  return { total, higher, lower, zeros, empty, deviations: higher + lower, detail };
}

// Validation — no hardcoded courier list; iterates tat/tatBlended keys.
export function validateAll(tat, tatBlended, modeByST, activeSts, groups, pricedKeys) {
  const issues = [];
  (activeSts || []).forEach((st) => {
    const stName = stNameFromGroups(st, groups);
    const mode = modeByST?.[st] || 'courier_wise';
    const check = (rowLabel, row) => {
      for (let z = 1; z <= 5; z += 1) {
        const zName = ZONES[z - 1].name;
        const mn = row[`z${z}_min`]; const mx = row[`z${z}_max`];
        if (mn === null || mn === undefined || mn === '') issues.push(`${stName} · ${rowLabel} · ${zName}: Min TAT empty`);
        else if (Number(mn) < 0) issues.push(`${stName} · ${rowLabel} · ${zName}: Min TAT cannot be negative`);
        if (mx === null || mx === undefined || mx === '') issues.push(`${stName} · ${rowLabel} · ${zName}: Max TAT empty`);
        else if (Number(mx) < 0) issues.push(`${stName} · ${rowLabel} · ${zName}: Max TAT cannot be negative`);
        if (mn != null && mn !== '' && mx != null && mx !== '' && Number(mn) > Number(mx)) {
          issues.push(`${stName} · ${rowLabel} · ${zName}: Min (${mn}d) cannot exceed Max (${mx}d)`);
        }
      }
    };
    if (mode === 'blended') {
      check('Blended', tatBlended[st] || {});
    } else {
      // Validate couriers that the user DID enter data for
      Object.keys(tat[st] || {}).forEach((parent) => check(parent, tat[st][parent] || {}));
      // Also flag couriers that are mapped in groups but have NO data at all —
      // but only those the merchant has pricing for (don't demand TAT for
      // couriers that were never priced/configured).
      const group = findStGroup(groups, st);
      const mappedParents = [...new Set(
        filterPricedCodes((group?.linkedCodes || []).filter((lc) => lc.isActive !== false), pricedKeys)
          .map((lc) => lc.parent || lc.cpAccountCode),
      )];
      mappedParents.forEach((parent) => {
        if (!tat[st]?.[parent]) issues.push(`${stName} · ${parent}: no TAT values entered (all 5 zones required)`);
      });
    }
  });
  return issues;
}

// CSV body — exact format per API 6 contract (Zone,Min Tat,Max Tat).
export function buildCsvBody(row) {
  const lines = ['Zone,Min Tat,Max Tat'];
  for (let z = 1; z <= 5; z += 1) {
    const mn = row?.[`z${z}_min`] ?? '';
    const mx = row?.[`z${z}_max`] ?? '';
    lines.push(`${z},${mn},${mx}`);
  }
  return lines.join('\n');
}

// Viewer rows — groups from GET /api/order/serviceability/intel/setup/groups.
export function buildViewerRows(tat, tatBlended, modeByST, activeSts, groups) {
  const rows = [];
  (activeSts || []).forEach((st) => {
    const group = findStGroup(groups, st);
    const linkedCodes = (group?.linkedCodes || []).filter((lc) => lc.isActive !== false);
    const apiGroup = group;
    const stName = apiGroup?.name || st;
    const stGroup = groupLabelFromApiGroup(apiGroup);
    const mode = modeByST?.[st] || 'courier_wise';
    const parents = [...new Set(linkedCodes.map((lc) => lc.parent || lc.cpAccountCode))];
    parents.forEach((parent) => {
      const codes = linkedCodes.filter((lc) => (lc.parent || lc.cpAccountCode) === parent);
      const payload = mode === 'blended' ? (tatBlended[st] || {}) : (tat[st]?.[parent] || {});
      codes.forEach((lc) => {
        for (let z = 1; z <= 5; z += 1) {
          rows.push({
            service_type: st, st_name: stName, group: stGroup, parent,
            account_code: lc.cpAccountCode, zone: z, zone_name: ZONES[z - 1].name,
            tat_min: payload[`z${z}_min`] ?? '', tat_max: payload[`z${z}_max`] ?? '', mode,
          });
        }
      });
    });
  });
  return rows;
}

export const SOURCE_COLORS = { agreement: '#06b6d4', override: '#a855f7', standard: '#94a3b8', manual: '#475569' };
export const DEVIATION_COLORS = { higher: { bg: '#fef2f2', fg: '#b91c1c' }, lower: { bg: '#dcfce7', fg: '#15803d' } };
